import axios from 'axios';
import { serverLog } from "../utils/ServerLog";

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  [중요] 쇼핑몰 상품연동을 테스트할 때는 아래 셋이 반드시 '같은 서버' 를 봐야 한다
 * ════════════════════════════════════════════════════════════════════════════════
 *   1) 모의쇼핑몰(mockshop.html)의 [API 서버] 선택
 *   2) 여기(웹)의 baseURL
 *   3) 앱(toelmobile/service/Api.js)의 baseURL
 *
 *  연동토큰은 '발급한 서버의 Redis 에만' 저장된다.
 *  하나라도 어긋나면 발급은 되는데 조회에서 못 찾아
 *  '연동정보가 만료되었습니다' 가 뜬다. (토큰이 만료된 게 아니라 다른 서버에 있는 것)
 * ════════════════════════════════════════════════════════════════════════════════
 */
axios.defaults.baseURL = 'https://btorage.com/api';              // 운영
// axios.defaults.baseURL = 'https://btorage-dev.com/api';          // 개발서버
// axios.defaults.baseURL = 'http://172.30.1.80:8080/api';             // 로컬 스프링부트 - 집 (앱과 동일하게 유지)
// axios.defaults.baseURL = 'http://192.168.0.11:8080/api';         // 로컬 스프링부트 - 사무실
// axios.defaults.baseURL = 'http://localhost:8080/api';            // PC 브라우저에서만 테스트할 때
axios.defaults.timeout = 60000; // 요청 제한 시간 (ms 단위)

// 요청 인터셉터 (필요 시 추가)
// axios.interceptors.request.use(
//     (config) => {
//         // 요청 전에 필요한 로직 추가 가능 (예: 토큰 추가)
//         console.log('Request sent to:', config.url);
//         return config;더
//     },
//     (error) => Promise.reject(error)
// );

const suppressedUrls = [
    '/holiday/selectedHolidayList/',
    '/credentials/secretManager/',
    '/init/accessInfo',
];

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  401 을 받으면 토큰을 갱신하고 원래 요청을 다시 보낸다
 * ════════════════════════════════════════════════════════════════════════════════
 *  [왜 필요한가]
 *   예전에는 토큰 갱신이 '타이머' 에만 의존했다. 그런데 타이머는 자주 놓친다.
 *     - 탭이 절전/백그라운드로 들어가면 브라우저가 타이머를 늦추거나 멈춘다
 *     - 컴퓨터를 껐다 켜면 그 사이 시간은 아무도 세지 않는다
 *     - 기기 시계가 바뀌면 경과시간 계산이 어긋난다
 *   그래서 만료된 액세스토큰으로 API 를 부르는 순간이 생긴다.
 *
 *   지금까지는 서버가 '만료된 AT 도 통과' 시켰기 때문에 이게 드러나지 않았다.
 *   그 보안구멍(S1)을 막으면서, 만료가 사용자에게 보이지 않도록 이 장치를 넣는다.
 *   401 을 받으면 조용히 갱신하고 같은 요청을 한 번 더 보낸다.
 *
 *  [무한루프 방지]
 *   - 재시도는 요청당 한 번만 (__retriedAfterReissue 표시)
 *   - 인증 관련 경로(재발급/로그인/로그아웃)는 재시도 대상에서 제외
 *   - 갱신 자체는 services/adminAuth.js, userAuth.js 가 inFlight + 탭간 잠금으로
 *     묶어두므로, 401 이 동시에 여러 개 떠도 갱신요청은 한 번만 나간다
 *
 *  [순환참조 회피]
 *   adminAuth/userAuth 가 이 파일을 import 하므로 여기서 그쪽을 import 할 수 없다.
 *   그래서 그쪽이 자기 갱신함수를 등록(registerTokenRefresher)하는 방식으로 뒤집었다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 재시도하지 않는 경로 (여기서 401 이 나면 그건 진짜 '세션 종료' 다) */
const NO_RETRY_URLS = [
    '/member/reissue',
    '/member/adminReissue',
    '/member/reissueMobile',
    '/member/login',
    '/member/logout',
    '/member/adminLogout',
    '/member/mobileLogout',
    '/storeMember/reissue',
];

/**
 * 서버로그 전송 경로인지.
 *
 * <p>오류처리에서 serverLog() 를 호출하고 serverLog 는 다시 '/log' 로 POST 하므로,
 * '/log' 자체가 실패하면 또 로그를 보내며 되돌이가 생길 수 있다.
 *
 * <p>[주의] includes('/log') 로 판정하면 안 된다. '/member/login' 에도 '/log' 가
 * 들어 있어서(…r/log+in) 로그인 실패까지 로그전송 대상에서 빠져버린다.
 * 경로의 '끝' 이 /log 인지로 정확히 본다.
 */
const isLogEndpoint = (url) => {
    const path = (url || '').split('?')[0].replace(/\/+$/, '');
    return path === '/log' || path.endsWith('/log');
};

/** {admin: fn, user: fn} — 각 모듈이 자기 갱신함수를 등록한다 */
const tokenRefreshers = {};

/**
 * 토큰 갱신함수 등록
 * @param scope 'admin' | 'user'
 * @param refresher () => Promise<boolean>   갱신 성공여부를 돌려준다
 * @param tokenGetter () => string|null      갱신 후 사용할 최신 토큰
 */
export const registerTokenRefresher = (scope, refresher, tokenGetter) => {
    // 함수가 아니면 '등록 안 된 것' 으로 둔다. 빈 객체를 남기면 아래 pickRefresher 가
    // 그걸 유효한 갱신수단으로 골라서 호출하다 터진다.
    if (typeof refresher !== 'function') {
        delete tokenRefreshers[scope];
        return;
    }
    tokenRefreshers[scope] = {refresher, tokenGetter};
};

/** 지금 화면이 관리자 영역인지 (App.js 의 판단과 같은 기준) */
const isAdminArea = () => {
    try {
        return window.location.pathname.startsWith('/btcorpad');
    } catch (e) {
        return false;
    }
};

/**
 * 지금 화면의 영역에 '해당하는' 갱신수단만 돌려준다.
 *
 * <p>════════════════════════════════════════════════════════════════════════
 *  [절대 반대 영역으로 폴백하지 않는다]
 * <p>════════════════════════════════════════════════════════════════════════
 *  예전에는 관리자 영역인데 관리자 갱신수단이 없으면 '사용자 갱신수단' 으로
 *  넘어갔다. 그러면 401 을 받은 관리자 요청이 '사용자 토큰' 을 달고 재시도된다.
 *  즉 관리자 동작이 사용자 권한으로 수행된다 — 권한 교차다.
 *
 *  갱신수단이 없으면 그냥 실패시킨다. 원래의 401 이 호출부로 전달되고,
 *  관리자 화면은 로그인으로 유도된다. 불편한 쪽이 위험한 쪽보다 낫다.
 * <p>════════════════════════════════════════════════════════════════════════
 */
const pickRefresher = () => {
    const scope = isAdminArea() ? 'admin' : 'user';
    return tokenRefreshers[scope] || null;
};

/**
 * 현재 영역(관리자/사용자)에 맞는 갱신함수로 액세스토큰을 갱신하고 새 토큰을 돌려준다.
 *
 * <p>[언제 쓰나] axios 를 거치지 않는 호출부가 401 을 받았을 때.
 *  대표적으로 결제완료 폴링 Web Worker 는 fetch 로 직접 두드리므로 아래 인터셉터의
 *  보호를 받지 못한다. 그 상태로 토큰이 만료되면 같은 토큰으로 영원히 401 을 받으며
 *  결제완료를 감지하지 못해 '결제누락' 이 된다. 그래서 수동으로 부를 창구를 둔다.
 *
 * <p>인터셉터와 '같은' 갱신수단(pickRefresher)을 쓰므로 동시에 여러 번 불려도
 *  adminAuth/userAuth 의 inFlight + 탭간 잠금에 묶여 재발급 요청은 한 번만 나간다.
 *
 * @return 갱신된 액세스토큰. 갱신수단이 없거나 실패하면 null
 */
export const refreshAccessToken = async () => {
    const entry = pickRefresher();
    if (!entry) return null;
    try {
        const renewed = await entry.refresher(true);
        if (!renewed) return null;
        return (typeof entry.tokenGetter === 'function' ? entry.tokenGetter() : null) || null;
    } catch (e) {
        console.log('[Auth] 수동 토큰갱신 실패 : ', e?.message || '');
        return null;
    }
};

const shouldRetry = (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    if (status !== 401 || !config) return false;
    if (config.__retriedAfterReissue) return false;
    const url = config.url || '';
    if (isLogEndpoint(url)) return false;   // 로그전송 실패는 재시도하지 않는다
    return !NO_RETRY_URLS.some((skip) => url.includes(skip));
};

axios.interceptors.response.use(
    (response) => response, // 정상 응답은 그대로 반환
    async (error) => {
        /*
         * 액세스토큰이 만료·폐기되어 401 을 받은 경우: 갱신 후 같은 요청을 한 번 더.
         * 성공하면 호출부는 401 이 있었다는 사실조차 모른다.
         */
        if (shouldRetry(error)) {
            const entry = pickRefresher();
            if (entry) {
                const config = error.config;
                config.__retriedAfterReissue = true;
                try {
                    // force=true — '경과시간' 이 아니라 '지금 만료됐다' 는 사실에 반응해야 한다
                    const renewed = await entry.refresher(true);
                    if (renewed) {
                        const token = entry.tokenGetter();
                        if (token) {
                            /*
                             * axios 1.x 의 config.headers 는 AxiosHeaders 인스턴스다.
                             * 스프레드로 새 객체를 만들면 내부 정규화가 깨질 수 있으므로
                             * set() 이 있으면 그걸 쓰고, 없을 때만 객체로 교체한다.
                             */
                            if (typeof config.headers?.set === 'function') {
                                config.headers.set('Authorization', token);
                            } else {
                                config.headers = {...(config.headers || {}), Authorization: token};
                            }
                        }
                        return axios(config);
                    }
                } catch (retryError) {
                    console.log('[Auth] 401 자동갱신 실패 — 원래 오류를 그대로 전달합니다.', retryError?.message || '');
                }
            }
        }

        const isNetworkError =
            !error.response &&
            (
                error.code === 'ECONNABORTED' ||                             // 타임아웃
                error.message?.includes('Network Error') ||                 // 일반 네트워크 에러
                error.message?.includes('Failed to fetch') ||              // fetch 실패 (브라우저 기본)
                error.message?.includes('timeout') ||                      // 커스텀 타임아웃 메시지
                error.message?.includes('SSL') ||                          // SSL 문제
                error.message?.includes('proxy') ||                        // 프록시 문제
                error.message?.includes('resolve')                         // DNS 오류
            );

        // ✅ 네트워크 발생 시
        if (isNetworkError) {
            // 요청 URL이 'suppressedUrls' 목록에 포함되어 있는지 확인 후 처리
            const url = error.config?.url || '';
            const isSuppressed = suppressedUrls.some(suppressedUrl => url.includes(suppressedUrl));

            // 실제 네트워크 오류가 아닌 함수 오류라면 전역 에러 페이지로 보내지 않고 로그만 기록
            if (navigator.onLine) {
                console.error("🚨 서버 응답 없음 (서버 Crash 또는 연결 끊김):", error.message, "URL:", url);
                // reject로 오류가 발생한 함수의 catch에서 처리위임
                return Promise.reject(error);
            }

            if (!isSuppressed) {
                // 'suppressedUrls'에 포함되지 않은 URL의 네트워크 오류일 때만 전역 이벤트 발생
                window.dispatchEvent(new Event('network-error'));
                console.log("전역 네트워크 오류 발생 (라우팅) : ", error.message, "URL:", url);
            } else {
                // 'suppressedUrls'에 포함된 URL은 로컬 .catch() 처리를 위해 로그만 기록
                console.log("무시된 네트워크 오류 (로컬 처리) : ", error.message, "URL:", url);
            }

        } else if (error.response) {
            const status = error.response.status;
            const url = error.config?.url || '';

            /*
             * 토큰 재발급 실패(401/400)는 '예상된 상황' 이다.
             *  세션이 끝났거나 쿠키가 없으면 서버가 401 을 주도록 설계돼 있고,
             *  services/userAuth.js / adminAuth.js 가 그것을 받아 세션을 정리한다.
             *  여기서 serverLog 까지 보내면 운영 서버로그가 이 건으로 뒤덮이므로
             *  (예전에는 5분마다 반복 호출되어 실제로 그랬다) 전송만 생략한다.
             *  콘솔에는 아래 console.error 로 남으니 추적은 그대로 가능하다.
             */
            const isExpectedReissueFail =
                (url.includes('/member/reissue') || url.includes('/member/adminReissue'))
                && (status === 401 || status === 400);
            if (isExpectedReissueFail) {
                return Promise.reject(error);
            }

            /*
             * 로그전송 경로가 실패했을 때 또 로그를 보내면 되돌이가 된다.
             * (serverLog -> POST /log -> 실패 -> serverLog -> ...)
             * 콘솔에만 남기고 넘어간다.
             */
            if (isLogEndpoint(url)) {
                return Promise.reject(error);
            }

            // 400, 500 에러는 오류컴포넌트 이동없이 로그기록만
            if (status === 400) {
                serverLog(`웹 Axios 400 오류 (URL: ${url}): `, error.response.data)
            } else if (status === 500) {
                serverLog(`웹 Axios 500 오류 (URL: ${url}): `, error.response.data)
            } else {
                serverLog(`웹 Axios ${status} 오류 (URL: ${url}):`, error.response.data)
            }
        }

        console.error('Axios error:', error.message || error);

        // 로컬 .catch()가 항상 실행되도록 오류전파
        return Promise.reject(error);
    }
);

export default axios;
